import { SpeechBubble } from '@/components/alisa/SpeechBubble';
import { Card } from '@/components/ui/card';
import { ALISA_IMAGES } from '@/data/moduleData';
import { useT } from '@/i18n';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';

const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

export const ChoiceScene = ({ scene, onChoice, onContinue }) => {
  const t = useT();
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  const shuffledChoices = useMemo(() => shuffleArray(scene.choices), [scene.id, scene.choices]);

  const handleSelect = (choice) => {
    if (selectedChoice) return;
    setSelectedChoice(choice);
    onChoice(scene.id, choice);
    setTimeout(() => setShowFeedback(true), 400);
  };

  const getChoiceStyle = (choice) => {
    if (!selectedChoice) {
      return 'border-border bg-card hover:border-primary/30 hover:shadow-soft active:scale-[0.98]';
    }
    if (choice.id === selectedChoice.id) {
      return choice.is_optimal
        ? 'border-success bg-success/5 shadow-soft'
        : 'border-destructive bg-destructive/5 shadow-soft';
    }
    if (choice.is_optimal && selectedChoice && !selectedChoice.is_optimal) {
      return 'border-success/40 bg-success/5 opacity-70';
    }
    return 'border-border bg-card opacity-40';
  };

  const getChoiceIcon = (choice) => {
    if (!selectedChoice) return null;
    if (choice.id === selectedChoice.id) {
      return choice.is_optimal
        ? <CheckCircle2 size={18} className="text-success shrink-0" />
        : <XCircle size={18} className="text-destructive shrink-0" />;
    }
    if (choice.is_optimal && !selectedChoice.is_optimal) {
      return <CheckCircle2 size={18} className="text-success/60 shrink-0" />;
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col flex-1"
    >
      {/* Scene image and Alisa Character Overlay */}
      <div className="relative w-full aspect-[16/9] overflow-hidden bg-muted">
        {scene.image && (
          <img
            src={scene.image}
            alt={scene.title}
            className="w-full h-full object-cover"
          />
        )}

        {/* Overlaid Alisa Character */}
        <motion.div
          key={showFeedback ? (selectedChoice?.is_optimal ? 'approval' : 'serious') : 'neutral'}
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          className="absolute left-0 bottom-0 h-[100%] z-10 pointer-events-none"
        >
          <img
            src={showFeedback && selectedChoice
              ? (selectedChoice.is_optimal ? ALISA_IMAGES.approval : ALISA_IMAGES.serious)
              : ALISA_IMAGES.neutral
            }
            alt={t('ui.alisa.name')}
            className="h-full w-auto object-contain object-bottom"
          />
        </motion.div>

        {/* Shadow Overlay for character readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent pointer-events-none" />
      </div>

      <div className="flex-1 px-4 py-4 flex flex-col gap-3">
        {/* Alisa's message (Instruction -> Feedback) */}
        <SpeechBubble
          text={showFeedback && selectedChoice ? selectedChoice.feedback : scene.alisa_text}
          emotion={showFeedback && selectedChoice
            ? (selectedChoice.is_optimal ? 'approval' : 'serious')
            : 'neutral'
          }
          key={showFeedback ? 'feedback' : 'instruction'}
        />

        {/* Choice options */}
        <div className="flex flex-col gap-2.5 mt-1">
          {shuffledChoices.map((choice, index) => (
            <motion.div
              key={choice.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
            >
              <Card
                className={`p-3.5 cursor-pointer border-2 transition-all duration-200 ${getChoiceStyle(choice)} ${selectedChoice ? 'cursor-default' : ''}`}
                onClick={() => handleSelect(choice)}
              >
                <div className="flex items-start gap-2.5">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0 text-xs font-bold text-muted-foreground font-heading uppercase">
                    {String.fromCharCode(65 + index)}
                  </span>
                  <p className="text-sm leading-relaxed text-card-foreground font-body flex-1">
                    {choice.text}
                  </p>
                  {getChoiceIcon(choice)}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Continue button (appears after choice) */}
      <AnimatePresence>
        {showFeedback && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="px-4 pb-4"
          >
            <button
              onClick={onContinue}
              className="w-full gradient-primary-btn text-primary-foreground font-heading font-bold text-base py-3.5 rounded-xl shadow-primary hover:shadow-primary-hover active:scale-[0.98] transition-transform duration-150 uppercase tracking-wide"
            >
              {t('ui.common.continue')}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
